import { ReactElement, useMemo } from 'react';
import {
    UIPaginatedFilterItem,
    UIPaginatedFilterOption,
    UIPopoverPlacement
} from '@/ui';
import { useCompanyContext } from '@/companies/providers';
import { useTaskFilter } from '../providers';
import { useFetchUserFilterOptions } from '@/common/queries';
import { useFilterPopupState } from '@/common/hooks';
import { useTasksTranslation } from '../hooks';

type UserFilterItemProps = {
    popoverPlacement?: UIPopoverPlacement;
};

export function UserFilterItem(props: UserFilterItemProps): ReactElement {
    const { popoverPlacement } = props;

    const { t } = useTasksTranslation();
    const { company } = useCompanyContext();
    const { teamIds, userIds, onUserFilterApplied } = useTaskFilter();
    const { search, setSearch, debouncedSearch, hasOpened, onOpenChanged } =
        useFilterPopupState();

    const scopedTeamIds = useMemo(() => Array.from(teamIds), [teamIds]);

    const {
        options,
        status,
        fetchNextPage,
        isFetchingNextPage,
        hasNextPage,
        refetch
    } = useFetchUserFilterOptions({
        companyId: company.id,
        search: debouncedSearch,
        teamIds: scopedTeamIds,
        enabled: hasOpened
    });

    const items = useMemo<UIPaginatedFilterOption[]>(() => {
        return options.map((option) => ({
            id: option.id,
            text: option.name,
            image: option.image ?? undefined
        }));
    }, [options]);

    const selectedIds = useMemo(() => Array.from(userIds), [userIds]);

    return (
        <UIPaginatedFilterItem
            noneSelectedText={t('components.user_filter_item.none_selected')}
            emptyItemsText={t('components.user_filter_item.empty')}
            icon="users"
            popoverPlacement={popoverPlacement}
            items={items}
            selectedIds={selectedIds}
            search={search}
            onSearchChange={setSearch}
            isLoading={hasOpened && status === 'pending'}
            isError={status === 'error'}
            isFetchingNextPage={isFetchingNextPage}
            hasNextPage={hasNextPage}
            onLoadMore={fetchNextPage}
            onOpenChanged={onOpenChanged}
            onApply={onUserFilterApplied}
            onRetry={refetch}
        />
    );
}
